import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import {
  createImageEntries,
  IMAGE_FILE_ACCEPT,
  MAX_IMAGE_SOURCE_BYTES,
} from '../utils/imageEntries';
import { useLang } from '../utils/i18n';

interface CenterImageControlProps {
  value?: string;
  onChange: (imageDataUrl: string | undefined) => void;
}

const CenterImageControl: React.FC<CenterImageControlProps> = ({ value, onChange }) => {
  const { t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // React Strict Mode replays effects in development; reset the guard on
    // every setup so a valid upload is not mistaken for an unmounted control.
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    // Allow selecting the same file again after a removal or failed attempt.
    event.currentTarget.value = '';
    if (!file) return;

    setBusy(true);
    setStatus(t('images.preparing'));
    setHasError(false);
    try {
      const result = await createImageEntries([file], {
        remainingEntries: 1,
        existingImageDataLength: 0,
      });
      if (!mountedRef.current) return;
      const imageDataUrl = result.entries[0]?.imageDataUrl;
      if (!imageDataUrl) {
        setHasError(true);
        setStatus(t('images.processError'));
        return;
      }
      onChange(imageDataUrl);
      setStatus(t('images.centerUpdated'));
    } catch {
      if (!mountedRef.current) return;
      setHasError(true);
      setStatus(t('images.processError'));
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const remove = () => {
    onChange(undefined);
    setHasError(false);
    setStatus(t('images.centerRemoved'));
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border-2 border-gray-200 bg-gray-50 shadow-inner dark:border-gray-600 dark:bg-gray-800">
          {value ? (
            <img
              src={value}
              alt={t('images.centerAlt')}
              className="h-full w-full object-cover"
              onLoad={event => { event.currentTarget.hidden = false; }}
              onError={event => { event.currentTarget.hidden = true; }}
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-500" aria-hidden="true">
              <ImagePlus size={21} />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('images.centerTitle')}</p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {t('images.centerDesc')}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={IMAGE_FILE_ACCEPT}
              hidden
              disabled={busy}
              onChange={handleFile}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              aria-busy={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-600 transition-colors hover:bg-violet-100 disabled:cursor-wait disabled:opacity-50 dark:bg-violet-900/20 dark:text-violet-400 dark:hover:bg-violet-900/40"
            >
              {busy ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <ImagePlus size={13} aria-hidden="true" />}
              {busy ? t('images.preparing') : value ? t('images.replace') : t('images.upload')}
            </button>
            {value && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              >
                <Trash2 size={13} aria-hidden="true" /> {t('images.remove')}
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
        {t('images.guidance', { size: Math.round(MAX_IMAGE_SOURCE_BYTES / 1024 / 1024) })}
      </p>
      <p
        role="status"
        aria-live="polite"
        className={`mt-1 min-h-4 text-[11px] ${hasError ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
      >
        {status}
      </p>
    </div>
  );
};

export default CenterImageControl;
